import { BillingDetails } from '@aaa-ncnu-ie/mypolicy-api-customers';
import { Box, Typography, Stack, useMediaQuery } from '@mui/material';
import { format } from 'date-fns';
import { useEffect, useState } from 'react';

import { usePoliciesQuery } from '../../../api/getPolicies';
import PaymentAmountRadio from '../../../components/PaymentAmountRadio';
import colors from '../../../constants/colors';
import {
  PaymentAmountType,
  PaymentAmountTypeLabels,
} from '../../../constants/enums';
import { usePaymentsContext } from '../../../store/payments';
import {
  SelectedPayment,
  updateCustomerPayments,
} from '../../../store/paymentsReducer';
import theme from '../../../styles/theme';
import {
  policyTypeMap,
  policyTypeMapSelector,
} from '../../../utils/policyTypeUtil';
import PayAnotherAmountField from '../../Payment/components/PayAnotherAmountField';
import { AmountFormValues } from '../../Payment/validation';

interface CardFormProps {
  bill: BillingDetails;
  paymentAmount: SelectedPayment;
  cardIndex: number;
}

const CardForm = ({ bill, paymentAmount, cardIndex }: CardFormProps) => {
  const { dispatch } = usePaymentsContext();
  const isMobile = useMediaQuery(theme.breakpoints.down('tablet'));

  const { data: policiesData } = usePoliciesQuery();
  const policy = policiesData?.policies?.find(
    (policy) => policy.policyNumber === bill.policyNumber
  );

  const [tempAmount, setTempAmount] = useState(paymentAmount?.amount ?? '');
  const [tempSelectedPaymentType, setTempSelectedPaymentType] = useState<
    PaymentAmountType | undefined
  >(paymentAmount.paymentType);

  useEffect(() => {
    paymentAmount.paymentType &&
      setTempSelectedPaymentType(paymentAmount.paymentType);

    if (
      paymentAmount?.paymentType === PaymentAmountType.ANOTHER_AMOUNT &&
      paymentAmount?.amount
    ) {
      setTempAmount(paymentAmount?.amount ?? '');
    }
  }, [paymentAmount]);

  const handlePaymentAmountSelection = (
    paymentType: PaymentAmountType,
    amount?: string
  ) => {
    const policyTitleIndex = policyTypeMapSelector(
      bill.policyTypeCd,
      bill.fullProductCode
    );
    const policyTitle = policyTypeMap[policyTitleIndex].title;
    dispatch(
      updateCustomerPayments({
        ...bill,
        policyNumber: bill.policyNumber,
        amount,
        paymentType,
        policyType: policyTitle,
        policyStatus: policy?.policyStatus ?? '',
      })
    );
  };

  const handleAnotherAmountSubmit = (values: AmountFormValues) => {
    handlePaymentAmountSelection(
      PaymentAmountType.ANOTHER_AMOUNT,
      values.amount
    );
  };

  return (
    <Stack
      direction="column"
      data-testid="paymentCardForm"
      sx={{
        backgroundColor: colors.csaaNeutralBackground,
        borderRadius: 2,
        p: {
          mobile: 2,
          tablet: 2,
          desktop: 3,
        },
        mt: {
          mobile: 2,
          tablet: 3,
        },
        mb: {
          mobile: 2,
          tablet: 2,
          desktop: 5,
        },
      }}
    >
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: {
            mobile: 16,
            tablet: 16,
            desktop: 18,
          },
          lineHeight: {
            mobile: '24px',
            tablet: '24px',
            desktop: '140%',
          },
          letterSpacing: {
            mobile: 0.3,
            tablet: 0.3,
            desktop: 0.4,
          },
          color: colors.csaaBlack,
          mb: {
            mobile: 2,
            tablet: 2,
            desktop: 3,
          },
        }}
      >
        Select payment amount
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: {
            mobile: 'column',
            tablet: 'row',
            desktop: 'row',
          },
          gap: 2,
          mb: {
            mobile: 2,
            tablet: 1,
            desktop: 1,
          },
        }}
      >
        <PaymentAmountRadio
          selectedValue={tempSelectedPaymentType}
          handleClick={(value) => {
            setTempSelectedPaymentType(value);
            handlePaymentAmountSelection(value, bill.minimumDueAmount);
          }}
          title={PaymentAmountTypeLabels.TOTAL_DUE}
          value={PaymentAmountType.TOTAL_DUE}
          amount={bill.minimumDueAmount}
          testId="pay-total-due-radio"
        />
        <PaymentAmountRadio
          selectedValue={tempSelectedPaymentType}
          handleClick={(value) => {
            setTempSelectedPaymentType(value);
            handlePaymentAmountSelection(value, bill.totalDueAmount);
          }}
          title={PaymentAmountTypeLabels.FULL}
          value={PaymentAmountType.FULL}
          amount={bill.totalDueAmount}
          testId="pay-in-full-radio"
        />
      </Box>
      {bill.dueDate && (
        <Typography
          sx={{
            display: {
              mobile: 'none',
              tablet: 'flex',
              desktop: 'flex',
            },
            fontSize: 16,
            lineHeight: '24px',
            fontWeight: 500,
            letterSpacing: 0.3,
            color: colors.csaaBlack,
            mb: 3,
          }}
        >
          Due {format(new Date(bill.dueDate), 'MM/dd/yyyy')}
        </Typography>
      )}
      <Box
        sx={{
          display: 'flex',
          flexDirection: {
            mobile: 'column',
            tablet: 'row',
            desktop: 'row',
          },
          gap: 2,
          width: '100%',
        }}
      >
        <PaymentAmountRadio
          selectedValue={tempSelectedPaymentType}
          data-testid="anotherAmount"
          handleClick={(value) => {
            setTempAmount('');
            setTempSelectedPaymentType(value);
          }}
          title={PaymentAmountTypeLabels.ANOTHER_AMOUNT}
          value={PaymentAmountType.ANOTHER_AMOUNT}
          testId="pay-another-amount-radio"
        />
        {isMobile &&
          tempSelectedPaymentType === PaymentAmountType.ANOTHER_AMOUNT && (
            <PayAnotherAmountField
              autoSubmit
              bill={bill}
              onSubmit={handleAnotherAmountSubmit}
              tempAmount={tempAmount}
              sx={{ mt: { mobile: 0 } }}
              testId={`pay-another-amount-field-${cardIndex}`}
            />
          )}
        <PaymentAmountRadio
          selectedValue={tempSelectedPaymentType}
          data-testid="noPayment"
          handleClick={(value) => {
            setTempSelectedPaymentType(value);
            handlePaymentAmountSelection(value);
          }}
          title={PaymentAmountTypeLabels.NO_PAYMENT}
          value={PaymentAmountType.NO_PAYMENT}
          testId="no-payment-radio"
        />
      </Box>
      {!isMobile &&
        tempSelectedPaymentType === PaymentAmountType.ANOTHER_AMOUNT && (
          <PayAnotherAmountField
            autoSubmit
            bill={bill}
            onSubmit={handleAnotherAmountSubmit}
            tempAmount={tempAmount}
            testId={`pay-another-amount-field-${cardIndex}`}
          />
        )}
    </Stack>
  );
};

export default CardForm;
